import { useCallback } from 'react';

import { miscActions } from '../../actions';
import { generalErrorHandler } from '../../actions/utils';
import { useAnalytics } from '../../helpers/analytics.helper';
import useDispatch from '../../hooks/useDispatch';
import { useFavStocks } from '../../hooks/useFavoriteStocks';
import { Category } from '../../py-generated/datalouder/stock/types';
import miscService from '../../services/misc.service';
import { Analytics } from '../../shared/types';
import { categoryToRegin } from '../stock/stockCategory';

export const useFavorite = (): [
  (category: Category, ticker: string) => boolean,
  (category: Category, ticker: string, callback?: () => void) => void,
  (category: Category, ticker: string) => boolean,
  (category: Category, ticker: string, callback?: () => void) => void,
] => {
  const dispatch = useDispatch();
  const { stocks } = useFavStocks();

  const [queue] = useAnalytics();

  const addStock = useCallback(
    (
      category: Category,
      ticker: string,
      unlike: boolean,
      callback?: () => void
    ) => {
      miscService.addFavoriteStockLegacy(
        ticker,
        unlike,
        categoryToRegin(category)
      );
      miscService
        .addFavoriteStock(category, ticker, unlike)
        .then(stock => {
          dispatch(miscActions.addFavStock(stock));
          callback && callback();
        })
        .catch(error => {
          generalErrorHandler(dispatch)(error);
          callback && callback();
        });
      queue(
        unlike ? Analytics.ActionEnum.Dislike : Analytics.ActionEnum.Like,
        ticker
      );
    },
    [dispatch, queue]
  );

  const removeStock = useCallback(
    (category: Category, ticker: string, callback?: () => void) => {
      miscService.removeFavoriteStockLegacy(ticker, categoryToRegin(category));
      miscService
        .removeFavoriteStock(category, ticker)
        .then(() => {
          dispatch(miscActions.removeFavStock(category, ticker));
          callback && callback();
        })
        .catch(error => {
          generalErrorHandler(dispatch)(error);
          callback && callback();
        });
      queue(Analytics.ActionEnum.CancelLikes, ticker);
    },
    [dispatch, queue]
  );

  const isLike = useCallback(
    (category: Category, ticker: string) =>
      stocks &&
      stocks
        .filter(s => !s.unlike)
        .some(s => s.ticker === ticker && s.category === category),
    [stocks]
  );

  const isUnlike = useCallback(
    (category: Category, ticker: string) =>
      stocks &&
      stocks
        .filter(s => s.unlike)
        .some(s => s.ticker === ticker && s.category === category),
    [stocks]
  );

  const toggleLike = useCallback(
    (category: Category, ticker: string, callback?: () => void) =>
      isLike(category, ticker)
        ? removeStock(category, ticker, callback)
        : addStock(category, ticker, false, callback),
    [addStock, isLike, removeStock]
  );

  const toggleUnlike = useCallback(
    (category: Category, ticker: string, callback?: () => void) =>
      isUnlike(category, ticker)
        ? removeStock(category, ticker, callback)
        : addStock(category, ticker, true, callback),
    [addStock, isUnlike, removeStock]
  );

  return [isLike, toggleLike, isUnlike, toggleUnlike];
};
